import { ref } from 'vue'

const MIN_DURATION_MS = 300
const MAX_DURATION_MS = 30000

// Push-to-talk recording via MediaRecorder. iOS Safari records audio/mp4,
// everything else falls back to audio/webm — the mime type travels with the
// blob so the Edge Function can forward it to Gemini.
export function useVoiceInput() {
  const isRecording = ref(false)
  const error = ref(null)

  let recorder = null
  let stream = null
  let chunks = []
  let startedAt = 0
  let maxDurationTimer = null
  let hardStoppedBlob = null

  function preferredMimeType() {
    if (MediaRecorder.isTypeSupported('audio/mp4')) return 'audio/mp4'
    if (MediaRecorder.isTypeSupported('audio/webm')) return 'audio/webm'
    return ''
  }

  function releaseStream() {
    stream?.getTracks().forEach((track) => track.stop())
    stream = null
  }

  async function start() {
    if (isRecording.value) return
    error.value = null

    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true
        }
      })
    } catch (e) {
      error.value = e?.name === 'NotAllowedError' ? 'permission' : 'unavailable'
      return
    }

    chunks = []
    const mimeType = preferredMimeType()
    recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
    recorder.ondataavailable = (event) => {
      if (event.data?.size) chunks.push(event.data)
    }
    recorder.start()
    startedAt = Date.now()
    isRecording.value = true
    hardStoppedBlob = null
    // Hard-stop at 30 s: finalize the recording but keep the blob so the
    // pointerup that follows still sends it.
    maxDurationTimer = setTimeout(async () => {
      hardStoppedBlob = await finalize()
    }, MAX_DURATION_MS)
  }

  // Resolves with the recorded Blob, or null for accidental taps (< 300 ms)
  // and when no recording is running.
  function stop() {
    clearTimeout(maxDurationTimer)

    if (hardStoppedBlob) {
      const blob = hardStoppedBlob
      hardStoppedBlob = null
      return Promise.resolve(blob)
    }

    return finalize()
  }

  function finalize() {
    if (!recorder || recorder.state === 'inactive') {
      releaseStream()
      isRecording.value = false
      return Promise.resolve(null)
    }

    const duration = Date.now() - startedAt
    const activeRecorder = recorder

    return new Promise((resolve) => {
      activeRecorder.onstop = () => {
        const mimeType = (activeRecorder.mimeType || 'audio/mp4').split(';')[0]
        releaseStream()
        isRecording.value = false
        recorder = null
        resolve(duration < MIN_DURATION_MS ? null : new Blob(chunks, { type: mimeType }))
      }
      activeRecorder.stop()
    })
  }

  return { isRecording, error, start, stop }
}
